import { Box, Text, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import CategoryForm from "./CategoryForm";
import { DataTable } from "../../../../../../components/DataTable";
import * as React from "react";
import { useEffect, useMemo } from "react";
import {
  organizationQuestionnaireKeys,
  useAttachFileToCategory, useDeleteCategoryAttachment,
  useGetCategoryAttachment
} from "../../../../../../hooks/query";
import {
  useUpdateUseOfSoldProducts
} from "../../../../../../hooks/query/questionnaire/categories/useOfSoldProducts";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import { FormTextArea } from "../../../../../../components/Forms";
import { useQueryClient } from "react-query";
import { ManualInput } from "../ManualInput";
import { FileAttachment } from "../../../FileAttachment";

function CategoryUseOfSoldProducts({
                                     defaultValues,
                                     handleCategoryChange,
                                     tabKey,
                                     finalize,
                                     finalizeQuestionnaire,
                                     isPreview
                                   }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const toast = useToast();
  const queryClient = useQueryClient();
  const {
    id: questionnaireId,
    categories: {
      useOfSoldProducts: {
        id: categoryId,
        items,
        notes,
        manualInputOfEmissions,
        totalCo2Emissions,
        attachment
      } = {
        id: null,
        items: [],
        notes: "",
        manualInputOfEmissions: false,
        totalCo2Emissions: null,
        attachment: null
      }
    }
  } = defaultValues;

  const useOfSoldProductsValues = {
    notes, manualInputOfEmissions, totalCo2Emissions
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    control,
    setValue,
    reset
  } = useForm({
    defaultValues: useOfSoldProductsValues
  });

  useEffect(() => {
    reset({
      notes, manualInputOfEmissions, totalCo2Emissions
    });
  }, [defaultValues, reset, notes, manualInputOfEmissions, totalCo2Emissions]);

  const formId = `formStep${tabKey}`;

  const columns = useMemo(
    () => [
      {
        header: t("inputs.useOfSoldProducts.gridColumns.brand"),
        accessorKey: "brand",
        size: 100
      },
      {
        header: t("inputs.useOfSoldProducts.gridColumns.itemNumber"),
        accessorKey: "itemNumber",
        size: 100,
        customStyles: {
          "whiteSpace": "pre-line"
        }
      },
      {
        header: t("inputs.useOfSoldProducts.gridColumns.itemDescription"),
        accessorKey: "itemDescription",
        size: 150,
        customStyles: {
          "whiteSpace": "pre-line"
        }
      },
      {
        header: t("inputs.useOfSoldProducts.gridColumns.gasConsumptionItem"),
        accessorKey: "gasConsumptionItem",
        size: 180,
        customStyles: {
          "whiteSpace": "pre-line"
        }
      },
      {
        header: t("inputs.useOfSoldProducts.gridColumns.electricityConsumptionItem"),
        accessorKey: "electricityConsumptionItem",
        size: 160,
        customStyles: {
          "whiteSpace": "pre-line"
        }
      },
      {
        header: t("inputs.useOfSoldProducts.gridColumns.averageExpectedLifeSpan"),
        accessorKey: "averageExpectedLifeSpan",
        size: 100,
        customStyles: {
          "whiteSpace": "pre-line"
        }
      },
      {
        header: t("inputs.useOfSoldProducts.gridColumns.numberOfItemSold"),
        accessorKey: "numberOfItemSold",
        size: 110,
        customStyles: {
          "whiteSpace": "pre-line"
        }
      }
    ],
    [t]
  );

  const isLoading = false;

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Use of Sold Products saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateUseOfSoldProducts(queryConfig);

  const onSubmit = ((data) => {
    if (!isPreview) {
      if (isDirty) {
        const requestData = {
          notes: data.notes,
          totalCo2Emissions: data.totalCo2Emissions,
          manualInputOfEmissions: data.manualInputOfEmissions
        };
        update({ questionnaireId, data: requestData });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      <Text opacity="0.75" fontSize="sm" marginBottom="4">
        {t(`categories.${tabKey}Desc`)}
      </Text>

      <DataTable
        data={items ?? []}
        columns={columns}
        isLoading={isLoading}
        containerHeight={items && items.length > 0 ? "calc(100% - 125px)" : "220px"}
      />

      <ManualInput
        register={register}
        errors={errors}
        control={control}
        watch={watch}
        setValue={setValue}
        isPreview={isPreview}
      />

      <FileAttachment
        type={"use-of-sold-products"}
        uploadHook={useAttachFileToCategory}
        downloadHook={useGetCategoryAttachment}
        deleteHook={useDeleteCategoryAttachment}
        invalidationKey={organizationQuestionnaireKeys.orgQuestionnaireById()}
        currentAttachment={attachment}
        params={{
          questionnaireId: questionnaireId,
          categoryId: categoryId
        }}
        isDisabled={isPreview}
      />

      <Box paddingTop="4">
        <FormTextArea
          label={t("inputs.notes")}
          errors={errors}
          {...register("notes")}
          maxHeight="50"
          disabled={isPreview}
        />
      </Box>

    </CategoryForm>
  );
}

export default CategoryUseOfSoldProducts;
